/** Match sampling: mm → WsMG equation; otherwise WsIP. */
export function isLengthUnitMm(lengthUnitName) {
  if (lengthUnitName == null || lengthUnitName === '') return false;
  return String(lengthUnitName).trim().toLowerCase() === 'mm';
}

/** Same logic as `calculateWsWr` in SamplingDetailViaPondDetail.js (Wr is weight/Ws ratio, not a percent). */
export function computePredatorWsWr({ lengthStr, weightStr, wsMG, wsIP, lengthUnitName }) {
  const equation = isLengthUnitMm(lengthUnitName)
    ? String(wsMG ?? '').trim()
    : String(wsIP ?? '').trim();
  const length = parseFloat(lengthStr);
  const weight = parseFloat(weightStr);

  if (!equation || Number.isNaN(length) || length <= 0) {
    return { ws: '', wr: '' };
  }

  try {
    if (!/^[0-9+\-*/(). L^log]*$/.test(equation)) {
      return { ws: '', wr: '' };
    }
    const replacedExpression = equation.replace(/L/g, length).replace(/\^/g, '**');
    const calculatedWs = Function(`return ${replacedExpression}`)();
    if (
      Number.isNaN(calculatedWs) ||
      !Number.isFinite(calculatedWs) ||
      calculatedWs <= 0
    ) {
      return { ws: '', wr: '' };
    }
    const ws = parseFloat(calculatedWs.toFixed(3));
    const wr =
      !Number.isNaN(weight) && weight > 0
        ? parseFloat((weight / calculatedWs).toFixed(3))
        : '';
    return { ws, wr };
  } catch {
    return { ws: '', wr: '' };
  }
}

export function stockingPredatorRowIsBatch(row) {
  if (!row) return false;
  return (
    row.IsBatch === true ||
    row.isBatch === true ||
    row.IsBatch === 1 ||
    row.isBatch === 1
  );
}

export function predatorRowLabelForExport(row, pondFishSpeciesOptions) {
  const sel = row.FishTypeID;
  if (sel == null || sel === '') return '';
  const opt = (pondFishSpeciesOptions || []).find(
    (o) => o.value === sel || String(o.value) === String(sel)
  );
  if (opt) return opt.label;
  return typeof sel === 'string' ? sel : String(sel);
}

/** Dense table label — prefer abbreviation when available on option or fish type. */
export function predatorRowTableLabel(row, pondFishSpeciesOptions, fishTypesById) {
  const sel = row.FishTypeID;
  const opt =
    sel != null && sel !== ''
      ? (pondFishSpeciesOptions || []).find(
          (o) => o.value === sel || String(o.value) === String(sel)
        )
      : null;
  const abbrFromOpt =
    opt?.abbreviation != null ? String(opt.abbreviation).trim() : '';
  if (abbrFromOpt) return abbrFromOpt;
  const ft =
    fishTypesById && sel != null && sel !== ''
      ? fishTypesById.get(Number(sel))
      : null;
  const abbrFromFt =
    (ft?.Abbreviation && String(ft.Abbreviation).trim()) ||
    (ft?.abbreviation && String(ft.abbreviation).trim()) ||
    '';
  if (abbrFromFt) return abbrFromFt;
  return predatorRowLabelForExport(row, pondFishSpeciesOptions);
}

/**
 * Per-row predator count.
 * Batch rows: Tag / Number encodes total headcount (same as forage).
 * Individual rows: Tag is a tag ID — count 1 fish per row (Number only if explicitly set).
 */
export function predatorRowHeadcount(row) {
  if (!row) return null;
  const numField =
    row.Number !== undefined && row.Number !== null
      ? row.Number
      : row.number !== undefined && row.number !== null
        ? row.number
        : row['Number'];
  const rawNumber =
    numField !== undefined && numField !== null && numField !== ''
      ? String(numField).trim()
      : '';
  const numDigits = rawNumber.replaceAll(',', '');
  const parsedFromNumber = rawNumber === '' ? NaN : parseInt(numDigits, 10);

  if (stockingPredatorRowIsBatch(row)) {
    const rawTag = row.Tag != null ? String(row.Tag).trim() : '';
    const tagDigits = rawTag.replaceAll(',', '');
    const parsedFromTag = rawTag === '' ? NaN : parseInt(tagDigits, 10);
    const n = !Number.isNaN(parsedFromTag)
      ? parsedFromTag
      : !Number.isNaN(parsedFromNumber)
        ? parsedFromNumber
        : null;
    if (n == null) return null;
    return Math.max(0, n);
  }

  // Individual row: one fish per entry; Tag is tag ID (not headcount).
  if (row.FishTypeID != null && row.FishTypeID !== '') return 1;
  return null;
}

function headcountForPredatorSummaryRow(row) {
  const n = predatorRowHeadcount(row);
  if (n != null && n > 0) return n;
  if (row.FishTypeID != null && row.FishTypeID !== '') return 1;
  return 0;
}

/**
 * Per fish-type totals from predator rows (individual + batch).
 */
export function buildPredatorOverallSummaries(
  predatorRows,
  pondFishSpeciesOptions,
  fishTypesById,
  lengthUnitName
) {
  const byKey = new Map();
  for (const row of predatorRows) {
    if (row.FishTypeID == null || row.FishTypeID === '') continue;
    const typeKey =
      typeof row.FishTypeID === 'number' && Number.isFinite(row.FishTypeID)
        ? `id:${Math.trunc(row.FishTypeID)}`
        : `id:${String(row.FishTypeID).trim()}`;
    const label =
      predatorRowTableLabel(row, pondFishSpeciesOptions, fishTypesById) ||
      `Type ${row.FishTypeID}`;
    const fullName =
      predatorRowLabelForExport(row, pondFishSpeciesOptions) ||
      `Type ${row.FishTypeID}`;
    const isBatch = stockingPredatorRowIsBatch(row);
    const w = row.Weight === '' || row.Weight == null ? NaN : parseFloat(row.Weight);
    const len = row.Length === '' || row.Length == null ? NaN : parseFloat(row.Length);
    const tw =
      row.TotalWeight === '' || row.TotalWeight == null ? NaN : parseFloat(row.TotalWeight);

    let g = byKey.get(typeKey);
    if (!g) {
      g = {
        typeKey,
        typeLabel: label,
        typeFullName: fullName,
        totalNumber: 0,
        totalWeight: 0,
        lengthNumerator: 0,
        lengthDenominator: 0,
        wrNumerator: 0,
        wrCount: 0,
      };
      byKey.set(typeKey, g);
    }

    const headN = headcountForPredatorSummaryRow(row);

    if (isBatch) {
      g.totalNumber += headN;
      if (Number.isFinite(tw)) {
        g.totalWeight += tw;
      }
      if (Number.isFinite(len) && headN > 0) {
        g.lengthNumerator += len * headN;
        g.lengthDenominator += headN;
      }
    } else {
      g.totalNumber += headN;
      if (Number.isFinite(w)) {
        g.totalWeight += w;
      }
      if (Number.isFinite(len)) {
        g.lengthNumerator += len;
        g.lengthDenominator += 1;
      }
      if (fishTypesById) {
        const ft = fishTypesById.get(Number(row.FishTypeID));
        const { wr } = computePredatorWsWr({
          lengthStr: row.Length,
          weightStr: row.Weight,
          wsMG: ft?.WsMG,
          wsIP: ft?.WsIP,
          lengthUnitName: lengthUnitName != null ? String(lengthUnitName) : '',
        });
        if (wr !== '' && wr != null) {
          const wrn = parseFloat(wr);
          if (Number.isFinite(wrn)) {
            g.wrNumerator += wrn;
            g.wrCount += 1;
          }
        }
      }
    }
  }
  return Array.from(byKey.values())
    .map((g) => ({
      typeKey: g.typeKey,
      typeLabel: g.typeLabel,
      typeFullName: g.typeFullName || g.typeLabel,
      totalNumber: g.totalNumber,
      totalWeight: g.totalWeight,
      avgLength:
        g.lengthDenominator > 0 ? g.lengthNumerator / g.lengthDenominator : null,
      avgWeight: g.totalNumber > 0 ? g.totalWeight / g.totalNumber : null,
      avgWr: g.wrCount > 0 ? g.wrNumerator / g.wrCount : null,
    }))
    .sort((a, b) =>
      String(a.typeLabel).localeCompare(String(b.typeLabel), undefined, {
        sensitivity: 'base',
      })
    );
}
